"use client";

import { useEffect, useRef, useState } from "react";
import ScrollFloat from "@/components/dubai/ui/ScrollFloat";

type Benefit = {
  title: string;
  description: string;
  icon: string;
};

const benefits: Benefit[] = [
  {
    title: "Company Formation",
    description:
      "We help select the business activities, prepare the application, submit the shareholder information and follow the file through to licence issuance.",
    icon: "icon-1.svg",
  },
  {
    title: "Visa and Emirates ID",
    description:
      "Dubai visa assistance and Emirates ID processing are included. Your company can be registered remotely, but you will need to visit Dubai for 1–2 days to complete the required visa and Emirates ID formalities.",
    icon: "icon-2.svg",
  },
  {
    title: "Banking and Payment Accounts",
    description:
      "We help you apply for multiple business accounts, including traditional UAE banks such as Mashreq and Emirates NBD, digital banking through Wio, and international payment platforms such as Payoneer.",
    icon: "icon-3.svg",
  },
];

const clamp = (value: number, minimum = 0, maximum = 1) =>
  Math.min(maximum, Math.max(minimum, value));

const interpolate = (start: number, end: number, progress: number) =>
  start + (end - start) * clamp(progress);

// How far right of its resting place a card starts, as a % of card width.
// Applied relative to each card's own target so all three slide the same
// distance: a fixed start offset made the right-hand cards travel up to 225%
// while the left one travelled 125%, so they crossed at very different speeds.
const ENTRY_TRAVEL = 110;

// Desktop choreography. Every value is in viewport heights of scrolling after
// the section pins, so the timeline reads in the order it plays:
//
//   0.0 - 2.2   heading slides in from the right, rests at centre, exits left
//               (ScrollFloat splits HEADING_TIMELINE into equal in/hold/out thirds)
//   2.3 - 3.3   card 1 flies in from the right and lands
//   3.4 - 4.4   card 2
//   4.5 - 5.5   card 3
//   5.5 - 6.0   everything holds before the section unpins
//
// The track height below must stay at (TIMELINE_END + 1) * 100vh — one extra
// screen because the sticky child only travels the track's height minus itself.
const HEADING_TIMELINE = 2.2;
const CARD_START = 2.3;
const CARD_GLIDE = 1;
const CARD_SPACING = 1.1;
const TIMELINE_END = 6;

const cardMotion = (progress: number, index: number) => {
  const start = CARD_START + index * CARD_SPACING;
  const entering = clamp((progress - start) / CARD_GLIDE);
  const easedEntering = 1 - Math.pow(1 - entering, 3);
  const targetX = index === 0 ? 0 : index === 1 ? -50 : -100;
  const rotation = index === 0 ? 15 : index === 1 ? -10 : 10;

  return {
    opacity: entering === 0 ? 0 : 0.15 + easedEntering * 0.85,
    translateX: interpolate(targetX + ENTRY_TRAVEL, targetX, easedEntering),
    scale: interpolate(0.96, 1, easedEntering),
    rotation: rotation * easedEntering,
    visible: progress >= start,
  };
};

export const IFZAEntrepreneurBenefitsSection = () => {
  const trackRef = useRef<HTMLDivElement>(null);
  const mobileRef = useRef<HTMLDivElement>(null);
  const [progress, setProgress] = useState(0);
  const [mobileRevealed, setMobileRevealed] = useState(false);

  useEffect(() => {
    const mobileSection = mobileRef.current;
    if (!mobileSection) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setMobileRevealed(true);
          observer.disconnect();
        }
      },
      { threshold: 0.12 },
    );

    observer.observe(mobileSection);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let frame = 0;

    const updateProgress = () => {
      frame = 0;
      const track = trackRef.current;
      if (!track) return;

      const rect = track.getBoundingClientRect();
      const viewport = window.innerHeight;
      const nextProgress =
        viewport > 0 ? clamp(-rect.top / viewport, 0, TIMELINE_END) : 0;

      setProgress((current) =>
        Math.abs(current - nextProgress) > 0.001 ? nextProgress : current,
      );
    };

    const scheduleUpdate = () => {
      if (!frame) frame = window.requestAnimationFrame(updateProgress);
    };

    updateProgress();
    window.addEventListener("scroll", scheduleUpdate, { passive: true });
    window.addEventListener("resize", scheduleUpdate);

    return () => {
      window.removeEventListener("scroll", scheduleUpdate);
      window.removeEventListener("resize", scheduleUpdate);
      window.cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <div ref={trackRef} className="relative h-auto w-full lg:h-[700vh]">
      <section
        aria-labelledby="what-we-handle-desktop what-we-handle-mobile"
        className="relative z-0 isolate w-full overflow-hidden bg-black lg:sticky lg:top-0 lg:h-screen"
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute left-1/2 top-1/2 -z-10 h-[1887px] w-[1887px] -translate-x-1/2 -translate-y-1/2 rounded-[336px] blur-[12.5px] [background:radial-gradient(50%_50%_at_50%_50%,rgba(13,109,250,0.2)_0%,rgba(13,109,250,0)_68%)]"
        />

        <div ref={mobileRef} className="flex w-full flex-col items-center gap-8 px-4 py-16 sm:gap-10 sm:px-8 sm:py-20 lg:hidden">
          <div className="inline-flex shrink-0 items-center gap-2 rounded-[99px] border border-[#0B3CFF4c] bg-[#0a1530] px-1.5 py-1 pr-2.5 backdrop-blur-[2.5px]">
            <img src="/dubai/box.svg" alt="" aria-hidden="true" className="h-5 w-5" />
            <span className="font-suisse-intl text-sm font-light leading-[17px] text-blue-200">
              End-to-end support
            </span>
          </div>

          <h2
            id="what-we-handle-mobile"
            className="w-full max-w-[560px]  bg-[linear-gradient(180deg,#1991fe_0%,#DAE8FF_20%,#1991fe_40%,#DAE8FF_100%)] bg-clip-text text-center font-suisse-intl text-[clamp(36px,9vw,64px)] font-normal leading-[1.15] text-transparent"
          >
            What We Handle
          </h2>

          <div className="flex w-full max-w-[460px] flex-col gap-5">
            {benefits.map((benefit, index) => (
              <div
                key={benefit.title}
                className={`relative overflow-hidden rounded-[32px] p-6 transition-[transform,opacity] duration-700 ease-out sm:p-8 ${mobileRevealed ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}
                style={{
                  background:
                    "linear-gradient(143deg, rgba(10,15,30,0.98) 0%, rgba(0,0,0,0.98) 100%)",
                  boxShadow:
                    "0 1px 0 1px rgba(249,250,249,0.06) inset, 0 24px 80px rgba(0,0,0,0.32)",
                  transitionDelay: `${index * 120}ms`,
                }}
              >
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute -left-10 -top-48 h-[420px] w-[500px] rounded-full bg-[radial-gradient(ellipse_at_center,rgba(13,109,250,0.22),transparent_68%)] blur-[18px]"
                />
                <div className="relative z-10 flex h-16 w-16 items-center justify-center rounded-[26px] bg-[rgba(13,109,250,0.1)] outline outline-1 -outline-offset-1 outline-[rgba(13,109,250,0.18)]">
                  <img
                    src={`/dubai/${benefit.icon}`}
                    alt=""
                    aria-hidden="true"
                    className="h-10 w-10"
                  />
                </div>
                <h3 className="relative z-10 m-0 pt-6 font-suisse-intl text-xl font-semibold leading-[28px] text-[#faf9fa] sm:text-2xl sm:leading-[31px]">
                  {benefit.title}
                </h3>
                <p className="relative z-10 m-0 pt-4 font-suisse-intl text-sm font-normal leading-6 text-[#a7a2ae] sm:text-base sm:leading-[26px]">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>

          <p className="m-0 max-w-[520px] text-center font-suisse-intl text-sm font-medium leading-[22px] text-[#a7a2ae]">
            Final bank and payment account approval always remains with the
            institution after its own KYC and compliance checks.
          </p>
        </div>

        <div className="absolute inset-0 mx-auto hidden max-w-[1240px] lg:block">
          <div className="absolute left-1/2 top-[calc(13.5%-30px)] z-40 flex w-full -translate-x-1/2 items-center justify-center gap-4 overflow-hidden">
            <img
              src="/dubai/left-design.svg"
              alt=""
              aria-hidden="true"
              className="hidden h-[6px] w-[114px] shrink-0 sm:block"
            />
            <div className="inline-flex shrink-0 items-center gap-2 rounded-[99px] border border-[#0B3CFF4c] bg-[#0a1530] px-1.5 py-1 pr-2.5 backdrop-blur-[2.5px]">
              <img src="/dubai/box.svg" alt="" aria-hidden="true" className="h-5 w-5" />
              <span className="font-suisse-intl text-sm font-light leading-[17px] text-blue-200">
                End-to-end support
              </span>
            </div>
            <img
              src="/dubai/right-design.svg"
              alt=""
              aria-hidden="true"
              className="hidden h-[6px] w-[114px] shrink-0 sm:block"
            />
          </div>

          <div
            // No -translate-x-1/2 here: Tailwind v4 emits that as the separate
            // `translate` CSS property, which composes with the inline
            // `transform` below rather than being overridden by it (v3 put both
            // on `transform`). The inline translate already centres this.
            className="absolute left-1/2 top-1/2 z-20 w-screen text-center"
            style={{
              transform: "translate(-50%, -50%)",
            }}
          >
            <ScrollFloat
              id="what-we-handle-desktop"
              triggerRef={trackRef}
              axis="x"
              animationDuration={2}
              scrub={1}
              ease="back.inOut(2)"
              scrollStart="top top"
              scrollEnd={`top top-=${HEADING_TIMELINE * 100}%`}
              stagger={0.03}
              containerClassName="m-0"
              textClassName=" bg-[linear-gradient(320deg,#1991fe_0%,#FFFFFF_100%)] bg-clip-text font-suisse-intl text-[clamp(48px,7vw,96px)] font-normal leading-[1.15] text-transparent"
            >
              What We Handle
            </ScrollFloat>
          </div>

          <div className="absolute inset-0 z-30 flex items-center justify-center">
            {benefits.map((benefit, index) => {
              const motion = cardMotion(progress, index);

              return (
                <article
                  key={benefit.title}
                  aria-hidden={!motion.visible}
                  // -translate-y-1/2 omitted for the same reason as the
                  // heading above: the inline transform's -50% is the only one
                  // that should apply.
                  className={`absolute top-1/2 w-[min(460px,calc(100%-32px))] overflow-hidden rounded-[32px] p-8 ${
                    index === 1 ? "h-[400px]" : "h-[350px]"
                  }`}
                  style={{
                    opacity: motion.opacity,
                    transform: `translate(${motion.translateX}%, -50%) rotate(${motion.rotation}deg) scale(${motion.scale})`,
                    left: index === 0 ? "0%" : index === 1 ? "50%" : "100%",
                    pointerEvents: motion.visible ? "auto" : "none",
                    background:
                      "linear-gradient(143deg, rgba(10,15,30,0.98) 0%, rgba(0,0,0,0.98) 100%)",
                    boxShadow:
                      "0 1px 0 1px rgba(249,250,249,0.06) inset, 0 24px 80px rgba(0,0,0,0.32)",
                    willChange: "transform, opacity",
                  }}
                >
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute -left-10 -top-48 h-[420px] w-[500px] rounded-full bg-[radial-gradient(ellipse_at_center,rgba(13,109,250,0.22),transparent_68%)] blur-[18px]"
                  />
                  <div className="relative z-10 flex h-16 w-16 items-center justify-center rounded-[26px] bg-[rgba(13,109,250,0.1)] outline outline-1 -outline-offset-1 outline-[rgba(13,109,250,0.18)]">
                    <img
                      src={`/dubai/${benefit.icon}`}
                      alt=""
                      aria-hidden="true"
                      className="h-10 w-10"
                    />
                  </div>
                  <h3 className="relative z-10 m-0 pt-8 font-suisse-intl text-2xl font-semibold leading-[31px] text-[#faf9fa]">
                    {benefit.title}
                  </h3>
                  <p className="relative z-10 m-0 pt-5 font-suisse-intl text-base font-normal leading-[26px] text-[#a7a2ae]">
                    {benefit.description}
                  </p>
                </article>
              );
            })}
          </div>
        </div>

        <p
          aria-live="polite"
          className="absolute bottom-7 left-1/2 z-40 m-0 hidden w-[min(90%,900px)] -translate-x-1/2 text-center font-suisse-intl text-sm font-medium leading-[22px] text-[#a7a2ae] lg:block"
        >
          Final bank and payment account approval always remains with the
          institution after its own KYC and compliance checks.
        </p>
      </section>
    </div>
  );
};
